import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Sleep Entries API
export const sleepAPI = {
  createEntry: (data) => api.post('/sleep-entries', data),
  getMyEntries: () => api.get('/sleep-entries'),
  getEntry: (id) => api.get(`/sleep-entries/${id}`),
  deleteEntry: (id) => api.delete(`/sleep-entries/${id}`),
  // Direct download URLs - opens in browser to trigger download
  getDownloadUrl: (entryId) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/sleep-entries/${entryId}/download?token=${token}`;
  },
  getDownloadAllUrl: () => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/my-entries/download-all?token=${token}`;
  },
};

// Trial API
export const trialAPI = {
  getTrials: () => api.get('/trials'),
  getActiveTrial: () => api.get('/trials/active'),
  getTrial: (trialId) => api.get(`/trials/${trialId}`),
  createTrial: (data) => api.post('/trials', data),
  completeTrial: (trialId) => api.put(`/trials/${trialId}/complete`),
  getTrialEntries: (trialId) => api.get(`/trials/${trialId}/entries`),
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getAllEntries: () => api.get('/admin/entries'),
  getStatistics: () => api.get('/admin/statistics'),
  getUserEntries: (userId) => api.get(`/admin/user/${userId}/entries`),
  deleteEntry: (entryId) => api.delete(`/admin/entry/${entryId}`),
  // Direct download URLs for admin
  getExportAllUrl: () => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/admin/export/csv?token=${token}`;
  },
  getMasterReportUrl: () => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/admin/master-report?token=${token}`;
  },
  getEntryDownloadUrl: (entryId) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/admin/entry/${entryId}/download?token=${token}`;
  },
  getUserEntriesDownloadUrl: (userId) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/admin/user/${userId}/download?token=${token}`;
  },
  getUserPdfReportUrl: (userId) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/admin/user/${userId}/download-pdf?token=${token}`;
  },
};

// Helper function to trigger download via hidden iframe/link
export const triggerDownload = (url) => {
  const token = localStorage.getItem('token');
  
  // Create a form to POST with auth header
  const form = document.createElement('form');
  form.method = 'GET';
  form.action = url;
  form.target = '_blank';
  
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

// Download with fetch and proper headers
export const downloadFile = async (url, filename) => {
  const token = localStorage.getItem('token');
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) throw new Error('Download failed');
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    window.URL.revokeObjectURL(downloadUrl);
    return true;
  } catch (error) {
    console.error('Download error:', error);
    return false;
  }
};

export default api;
